// MESERI 1985 "tal cual el método" (PE-18, Kevin 26/10/2026): Jesús Pérez Obeso, "Evaluación del
// riesgo de incendio: método simplificado", Gerencia de Riesgos y Seguros, MAPFRE, 1985.
// Factores X transcritos del plan de referencia de la Empresa A (p. 22 y 23; PASO0_DISENO.md §3.3.2).
// Fórmula (PE-13): P = 5X/129 + 5Y/26 + B; 26 es el único divisor coherente con Y máximo = 26. Matriz, fila 1.14.

import { doc, unirFuentes } from './pe-tipos';

export const FUENTE_MESERI = {
  referencia: 'Pérez Obeso, J. (1985). Evaluación del riesgo de incendio: método simplificado. Gerencia de Riesgos y Seguros, MAPFRE',
  formula: { norma: 'MESERI 1985 (método técnico aceptado)', fuente_doc: doc.matriz(33) }, // PE-10 y PE-13
  factores: { norma: 'MESERI 1985 (Pérez Obeso, MAPFRE)', fuente_doc: unirFuentes(doc.paso0('§3.3.2'), doc.paso0('§7 PE-18')) },
};

export type FactorX =
  | 'pisos' | 'superficie' | 'resistencia_fuego' | 'falsos_techos'
  | 'distancia_bomberos' | 'accesibilidad'
  | 'peligro_activacion' | 'carga_termica' | 'tipo_combustible' | 'orden_limpieza' | 'almacenamiento_altura'
  | 'concentracion'
  | 'propagabilidad_vertical' | 'propagabilidad_horizontal'
  | 'destructibilidad_calor' | 'destructibilidad_humo' | 'destructibilidad_corrosion' | 'destructibilidad_agua';

export interface OpcionFactor { clave: string; etiqueta: string; puntos: number }

const bma = (alta: number, media: number, baja: number): OpcionFactor[] => [
  { clave: 'baja', etiqueta: 'Baja', puntos: baja },
  { clave: 'media', etiqueta: 'Media', puntos: media },
  { clave: 'alta', etiqueta: 'Alta', puntos: alta },
];

// Los 18 factores X con sus opciones (X máximo = 129).
export const FACTORES_X: Record<FactorX, { grupo: string; etiqueta: string; opciones: OpcionFactor[] }> = {
  pisos: { grupo: 'Construcción', etiqueta: 'N.º de pisos / altura', opciones: [
    { clave: '1-2', etiqueta: '1 o 2 / menor de 6 m', puntos: 3 },
    { clave: '3-5', etiqueta: '3, 4 o 5 / entre 6 y 15 m', puntos: 2 },
    { clave: '6-9', etiqueta: '6, 7, 8 o 9 / entre 15 y 27 m', puntos: 1 },
    { clave: '10+', etiqueta: '10 o más / más de 30 m', puntos: 0 },
  ] },
  superficie: { grupo: 'Construcción', etiqueta: 'Superficie del mayor sector de incendios', opciones: [
    { clave: '0-500', etiqueta: 'De 0 a 500 m²', puntos: 5 },
    { clave: '501-1500', etiqueta: 'De 501 a 1500 m²', puntos: 4 },
    { clave: '1501-2500', etiqueta: 'De 1501 a 2500 m²', puntos: 3 },
    { clave: '2501-3500', etiqueta: 'De 2501 a 3500 m²', puntos: 2 },
    { clave: '3501-4500', etiqueta: 'De 3501 a 4500 m²', puntos: 1 },
    { clave: '4500+', etiqueta: 'Más de 4500 m²', puntos: 0 },
  ] },
  resistencia_fuego: { grupo: 'Construcción', etiqueta: 'Resistencia al fuego', opciones: [
    { clave: 'resistente', etiqueta: 'Resistente al fuego (hormigón)', puntos: 10 },
    { clave: 'no_combustible', etiqueta: 'No combustible (metálico)', puntos: 5 },
    { clave: 'combustible', etiqueta: 'Combustible (madera)', puntos: 0 },
  ] },
  falsos_techos: { grupo: 'Construcción', etiqueta: 'Falsos techos', opciones: [
    { clave: 'sin', etiqueta: 'Sin falsos techos', puntos: 5 },
    { clave: 'incombustibles', etiqueta: 'Con falsos techos incombustibles', puntos: 3 },
    { clave: 'combustibles', etiqueta: 'Con falsos techos combustibles', puntos: 0 },
  ] },
  // Se calcula con distanciaBomberosPuntos() desde km y minutos.
  distancia_bomberos: { grupo: 'Situación', etiqueta: 'Distancia de los bomberos', opciones: [
    { clave: '<5', etiqueta: 'Menor de 5 km / 5 min', puntos: 10 },
    { clave: '5-10', etiqueta: 'Entre 5 y 10 km / 5 y 10 min', puntos: 8 },
    { clave: '10-15', etiqueta: 'Entre 10 y 15 km / 10 y 15 min', puntos: 6 },
    { clave: '15-25', etiqueta: 'Entre 15 y 25 km / 15 y 25 min', puntos: 2 },
    { clave: '>25', etiqueta: 'Más de 25 km / 25 min', puntos: 0 },
  ] },
  accesibilidad: { grupo: 'Situación', etiqueta: 'Accesibilidad del edificio', opciones: [
    { clave: 'buena', etiqueta: 'Buena', puntos: 5 },
    { clave: 'media', etiqueta: 'Media', puntos: 3 },
    { clave: 'mala', etiqueta: 'Mala', puntos: 1 },
    { clave: 'muy_mala', etiqueta: 'Muy mala', puntos: 0 },
  ] },
  peligro_activacion: { grupo: 'Procesos', etiqueta: 'Peligro de activación', opciones: [
    { clave: 'bajo', etiqueta: 'Bajo (elementos no combustibles o retardantes)', puntos: 10 },
    { clave: 'medio', etiqueta: 'Medio (tiene maderas)', puntos: 5 },
    { clave: 'alto', etiqueta: 'Alto (textiles, papeles, pinturas inflamables)', puntos: 0 },
  ] },
  // Se calcula con cargaTermicaPuntos() desde el Qs del sector.
  carga_termica: { grupo: 'Procesos', etiqueta: 'Carga térmica', opciones: [
    { clave: 'baja', etiqueta: 'Baja (Q < 100 Mcal/m²)', puntos: 10 },
    { clave: 'media', etiqueta: 'Media (100 < Q < 200 Mcal/m²)', puntos: 5 },
    { clave: 'alta', etiqueta: 'Alta (Q > 200 Mcal/m²)', puntos: 0 },
  ] },
  tipo_combustible: { grupo: 'Procesos', etiqueta: 'Tipo de combustible', opciones: [
    { clave: 'baja', etiqueta: 'Baja (M.0 y M.1)', puntos: 5 },
    { clave: 'media', etiqueta: 'Media (M.2 y M.3)', puntos: 3 },
    { clave: 'alta', etiqueta: 'Alta (M.4 y M.5)', puntos: 0 },
  ] },
  orden_limpieza: { grupo: 'Procesos', etiqueta: 'Orden y limpieza', opciones: [
    { clave: 'bajo', etiqueta: 'Bajo (lugares sucios y desordenados)', puntos: 0 },
    { clave: 'medio', etiqueta: 'Medio (procedimientos irregulares)', puntos: 5 },
    { clave: 'alto', etiqueta: 'Alto (programas aplicados, por ejemplo 5S)', puntos: 10 },
  ] },
  almacenamiento_altura: { grupo: 'Procesos', etiqueta: 'Almacenamiento en altura', opciones: [
    { clave: '<2', etiqueta: 'Menor de 2 m', puntos: 3 },
    { clave: '2-4', etiqueta: 'Entre 2 y 4 m', puntos: 2 },
    { clave: '>6', etiqueta: 'Más de 6 m', puntos: 0 },
  ] },
  concentracion: { grupo: 'Concentración', etiqueta: 'Valor concentrado', opciones: [
    { clave: '<400', etiqueta: 'Menor de $400/m²', puntos: 3 },
    { clave: '400-1600', etiqueta: 'Entre $400 y $1600/m²', puntos: 2 },
    { clave: '>1600', etiqueta: 'Más de $1600/m²', puntos: 0 },
  ] },
  propagabilidad_vertical: { grupo: 'Propagabilidad', etiqueta: 'Vertical', opciones: bma(0, 3, 5) },
  propagabilidad_horizontal: { grupo: 'Propagabilidad', etiqueta: 'Horizontal', opciones: bma(0, 3, 5) },
  destructibilidad_calor: { grupo: 'Destructibilidad', etiqueta: 'Por calor', opciones: bma(0, 5, 10) },
  // En el plan dice "Por Calor" dos veces; el segundo es "Por humo" (PE-18e).
  destructibilidad_humo: { grupo: 'Destructibilidad', etiqueta: 'Por humo', opciones: bma(0, 5, 10) },
  destructibilidad_corrosion: { grupo: 'Destructibilidad', etiqueta: 'Por corrosión', opciones: bma(0, 5, 10) },
  destructibilidad_agua: { grupo: 'Destructibilidad', etiqueta: 'Por agua', opciones: bma(0, 5, 10) },
};

export type MedioY = 'extintores' | 'bie' | 'hidrantes_exteriores' | 'deteccion' | 'rociadores' | 'instalaciones_fijas';

// SV: sin vigilancia; CV: con vigilancia. Y máximo = 26 (suma de CV). Tabla estándar de MESERI,
// aceptada por Kevin (PE-18); los rangos "4-3" y "8-7" del plan de referencia quedan descartados.
export const MEDIOS_Y: Record<MedioY, { etiqueta: string; sv: number; cv: number }> = {
  extintores: { etiqueta: 'Extintores portátiles (EXT)', sv: 1, cv: 2 },
  bie: { etiqueta: 'Bocas de incendio equipadas (BIE)', sv: 2, cv: 4 },
  hidrantes_exteriores: { etiqueta: 'Columnas hidrantes exteriores (CHE)', sv: 2, cv: 4 },
  deteccion: { etiqueta: 'Detección automática (DET)', sv: 0, cv: 4 },
  rociadores: { etiqueta: 'Rociadores automáticos (ROC)', sv: 5, cv: 8 },
  instalaciones_fijas: { etiqueta: 'Extinción por agentes gaseosos / instalaciones fijas (IFE)', sv: 2, cv: 4 },
};

// Escala de P. Por validar hasta que Kevin confirme la fuente (PE-18a y PE-18b).
// Escala de P (PE-18): 0–2 muy grave, 2,1–4 grave, 4,1–6 medio, 6,1–8 leve, 8,1–10 muy leve
// ("hasta" incluido; un P entre 2 y 2,1 cae en el tramo siguiente). Aceptable P > 5, no aceptable
// P < 5; P = 5 exacto queda "en el límite" hasta que Kevin responda (PE-35).
export const ESCALA_MESERI = {
  limiteAceptable: 5,
  cualitativa: [
    { hasta: 2, etiqueta: 'muy grave' },
    { hasta: 4, etiqueta: 'grave' },
    { hasta: 6, etiqueta: 'medio' },
    { hasta: 8, etiqueta: 'leve' },
    { hasta: Infinity, etiqueta: 'muy leve' },
  ],
};
